// DCI (Legionella Dashboard) specific flow: This file will be removed once API for DCI (Legionella Dashboard) is unified in BED.
import { ChatContent } from '../models/chat.model';
import { EvaDciQueryParams, EvaDciResponse } from '../models/eva-request.model';

const EVA_LEGIONELLA_CONTEXT_STORAGE_KEY = 'eva.legionella.context';
const LEGIONELLA_PARSE_ERROR_MESSAGE = 'Sorry, I could not parse the response. Please try again.';

export function getLegionellaEvaDciContext(): EvaDciQueryParams {
  const defaultContext = getDefaultLegionellaEvaDciContext();

  if (typeof window === 'undefined') {
    return defaultContext;
  }

  try {
    const value = sessionStorage.getItem(EVA_LEGIONELLA_CONTEXT_STORAGE_KEY);
    if (!value) {
      return defaultContext;
    }

    const parsed = JSON.parse(value);
    const siteIds = Array.isArray(parsed?.siteIds)
      ? parsed.siteIds
        .map((siteId: unknown) => Number(siteId))
        .filter((siteId: number) => !Number.isNaN(siteId))
      : [];
    const corporateCpId = Number(parsed?.corporateCpId ?? 0);

    return {
      siteIds,
      startDate: typeof parsed?.startDate === 'string' && parsed.startDate.length ? parsed.startDate : defaultContext.startDate,
      endDate: typeof parsed?.endDate === 'string' && parsed.endDate.length ? parsed.endDate : defaultContext.endDate,
      corporateCpId: Number.isNaN(corporateCpId) ? 0 : corporateCpId
    };
  } catch {
    return defaultContext;
  }
}

export function getLegionellaSessionId(response: EvaDciResponse | null): string | null {
  if (!response) {
    return null;
  }

  const sessionId = response.SessionId;
  return typeof sessionId === 'string' && sessionId.trim().length ? sessionId : null;
}

export function getLegionellaResponseContent(response: EvaDciResponse | null): ChatContent {
  const responseText = getLegionellaResponseText(response);

  if (containsLegionellaMermaid(responseText)) {
    return {
      type: 'text',
      data: LEGIONELLA_PARSE_ERROR_MESSAGE
    };
  }

  if (!containsLegionellaTable(responseText)) {
    return {
      type: 'text',
      data: responseText
    };
  }

  return {
    type: 'html',
    data: convertLegionellaMarkdownToHtml(responseText)
  };
}

function getDefaultLegionellaEvaDciContext(): EvaDciQueryParams {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 365);

  return {
    siteIds: [],
    startDate: formatDateValue(startDate),
    endDate: formatDateValue(endDate),
    corporateCpId: 0
  };
}

function formatDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getLegionellaResponseText(response: EvaDciResponse | null): string {
  if (!response) {
    return LEGIONELLA_PARSE_ERROR_MESSAGE;
  }

  const responseText = response.Message;

  if (typeof responseText === 'string' && responseText.trim().length) {
    return responseText;
  }

  return LEGIONELLA_PARSE_ERROR_MESSAGE;
}

function containsLegionellaMermaid(message: string): boolean {
  return /```mermaid[\s\S]*?```/i.test(message);
}

function containsLegionellaTable(message: string): boolean {
  return /\|.*\|\s*\n\|\s*[-:]+/i.test(message);
}

function convertLegionellaMarkdownToHtml(markdown: string): string {
  const normalizedMarkdown = (markdown || '').replace(/\r\n/g, '\n').trim();
  return renderTextWithMarkdownTables(normalizedMarkdown);
}

function renderTextWithMarkdownTables(content: string): string {
  const lines = content.split('\n');
  const fragments: string[] = [];
  let index = 0;

  while (index < lines.length) {
    if (isMarkdownTableStart(lines, index)) {
      const tableLines: string[] = [];

      while (index < lines.length && looksLikeMarkdownTableLine(lines[index])) {
        tableLines.push(lines[index]);
        index++;
      }

      fragments.push(convertMarkdownTableToHtml(tableLines));
      continue;
    }

    const textLines: string[] = [];
    while (index < lines.length && !isMarkdownTableStart(lines, index)) {
      textLines.push(lines[index]);
      index++;
    }

    const textBlock = renderTextBlock(textLines.join('\n'));
    if (textBlock) {
      fragments.push(textBlock);
    }
  }

  return fragments.join('');
}

function isMarkdownTableStart(lines: string[], index: number): boolean {
  if (index + 1 >= lines.length) {
    return false;
  }

  const header = lines[index];
  const separator = lines[index + 1];

  return looksLikeMarkdownTableLine(header) && isMarkdownTableSeparator(separator);
}

function looksLikeMarkdownTableLine(line: string): boolean {
  if (!line) {
    return false;
  }

  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

function isMarkdownTableSeparator(line: string): boolean {
  if (!line) {
    return false;
  }

  const separatorPattern = /^\|\s*[:\-]+\s*(\|\s*[:\-]+\s*)+\|\s*$/;
  return separatorPattern.test(line.trim());
}

function convertMarkdownTableToHtml(tableLines: string[]): string {
  if (tableLines.length < 2) {
    return renderTextBlock(tableLines.join('\n'));
  }

  const rows = tableLines
    .map(line => parseMarkdownTableRow(line))
    .filter(row => row.length > 0);

  if (rows.length < 2) {
    return renderTextBlock(tableLines.join('\n'));
  }

  const headers = rows[0];
  const bodyRows = rows.slice(2);

  const headerHtml = headers
    .map(cell => `<th>${escapeHtml(cell)}</th>`)
    .join('');

  const bodyHtml = bodyRows
    .map(row => {
      const paddedRow = [...row];
      while (paddedRow.length < headers.length) {
        paddedRow.push('');
      }

      return `<tr>${paddedRow.slice(0, headers.length).map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`;
    })
    .join('');

  return [
    '<div class="eva-legionella-table-container">',
    '<table class="eva-legionella-table">',
    `<thead><tr>${headerHtml}</tr></thead>`,
    `<tbody>${bodyHtml}</tbody>`,
    '</table>',
    '</div>'
  ].join('');
}

function parseMarkdownTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());
}

function renderTextBlock(text: string): string {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return '';
  }

  return `<div class="eva-legionella-text">${escapeHtml(trimmed).replace(/\n/g, '<br>')}</div>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}